import {noop} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BulkAddRowsToDataTableFlow from 'bulk-import/results/BulkAddRowsToDataTableFlow';
import BulkUpdateDataTableFlow from 'bulk-import/results/BulkUpdateDataTableFlow';
import {COLUMN_TYPE} from 'bulk-import/SpreadsheetBulkImportConstants';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DataTableConfigContextHandler from 'notebook/data-tables/DataTableConfigContextHandler';
import {getColumnTypeFromSchema, getUnusedSchemaFields} from 'notebook/data-tables/DataTableConfigEditUtil';
import {
  DATA_TABLE_TYPE,
  DataTable,
  dataTable as DATA_TABLE_SHAPE,
} from 'notebook/data-tables/DataTableConstants';
import DataTableEditableConnector, {
  DataTableEditableEnhancedProps,
} from 'notebook/data-tables/DataTableEditableConnector';
import {insertRowsFromSpreadsheet, updateRowsFromSpreadsheet} from 'notebook/data-tables/DataTableImportUtil';
import {AddMenuItem} from 'notebook/data-tables/DataTableTypes';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import DataTableBatchLinkContext from 'notebook/data-tables/link-contexts/DataTableBatchLinkContext';
import DataTableFileLinkContext from 'notebook/data-tables/link-contexts/DataTableFileLinkContext';
import DataTableNoLinkContext from 'notebook/data-tables/link-contexts/DataTableNoLinkContext';
import DataTableStageRunSampleLinkContext from 'notebook/data-tables/link-contexts/DataTableStageRunSampleLinkContext';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {NoteEditor, NoteEditorProps, UpdateHandler} from 'notebook/notes/NoteEditorTypes';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import {
  REGISTRATION_TABLE_SHAPE,
  RegistrationTable,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {REVIEW_SHAPE} from 'notebook/reviews/ReviewCriteriaConstants';
import {getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import ManageSourcesModal from 'notebook/structured-tables/components/modals/ManageSourcesModal';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import {SchemaFieldColumnUpdate} from 'notebook/structured-tables/StructuredTableConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import type {Selector} from 'notebook/tables/excess/Evaluator';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {handleUpdateLookupFieldValues} from 'notebook/tables/lookup-field/LookupFieldUtil';
import {
  handleRefreshDataTableData,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {
  CellColors,
  Cells,
  ColumnConstraint,
  MultiSelectionBox,
  Table,
  TableData,
  TableFunction,
  TableHeaderControlsArgs,
  TableIndex,
  TableOpDiffMap,
  UPDATE_LOOKUP_FIELD_MESSAGE,
} from 'notebook/tables/TableConstants';
import {
  getLockedTableWarningMessage,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';
import convertFileToCellData from 'notebook/util/ConvertFileToCellData';
import {ASSAY_RECORD_SHAPE, OVERSIZED_RESULTS_TABLE_TOOLTIP} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import FormulaBoxSetValidationButton from 'results/validation/FormulaBoxSetValidationButton';
import getTableWithValidationIndicators, {
  ValidationIndicatorGenerationData,
} from 'results/validation/getTableWithValidationIndicators';
import {ASSAY_RESULT_VALIDATION_SHAPE} from 'results/validation/ValidationConstants';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import APIUtil from 'util/APIUtil';
import manageRowState, {RowState, UpdateEachRowStateFunc, UpdateRowStateFunc} from 'util/manageRowState';
import memoizeLastCall from 'util/memoizeLastCall';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {Optional} from 'util/Types';
import {autopopulateWithNewSources} from 'workflows/util/SourceSinkAutopopulationUtil';
import {
  INPUT_SAMPLES_SOURCE_UUID,
  REGISTRATION_RECORD_LIST_SHAPE,
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
} from 'workflows/WorkflowConstants';

type SampleIds = Array<string> | null;

type AnyDataTableLinkContext =
  | typeof DataTableStageRunSampleLinkContext
  | typeof DataTableBatchLinkContext
  | typeof DataTableFileLinkContext
  | typeof DataTableNoLinkContext;

interface ExtendedColumnConstraint extends ColumnConstraint {
  viewer: boolean;
}

interface TransformedTable extends Omit<Table, 'columnConstraints'> {
  columnConstraints: Array<ExtendedColumnConstraint | {viewer: boolean}>;
  disallowCustomColumns: boolean;
}

function transformTableForRun(table: Table): TransformedTable {
  return {
    ...table,
    columnConstraints: table.columnConstraints.map((columnConstraint) => ({
      ...columnConstraint,
      viewer: true,
    })),
    disallowCustomColumns: true,
    allowRowRemoval: false,
  };
}

interface ExtendedRowState extends RowState {
  selected: boolean;
}

export interface RowStateManagerProps {
  rowStates: Array<ExtendedRowState>;
  updateRowState: UpdateRowStateFunc<ExtendedRowState>;
  updateEachRowState: UpdateEachRowStateFunc<ExtendedRowState>;
}

const rowStateManager = manageRowState<DataTableEditableProps, ExtendedRowState>({
  defaultState: {selected: false},
  mapPropsToOrderedIds(ownProps: DataTableEditableProps): Array<string> {
    return ownProps.dataTable.rows.map((row) => row.euid);
  },
  mapStateToProps(
    rowStates: Array<ExtendedRowState>,
    updateRowState: UpdateRowStateFunc<ExtendedRowState>,
    updateEachRowState: UpdateEachRowStateFunc<ExtendedRowState>
  ): RowStateManagerProps {
    return {rowStates, updateRowState, updateEachRowState};
  },
});

interface UpdateHandlerInfo {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectors?: any;
}

export interface DataTableEditableProps extends MediocreSearchResultProps {
  fileId: string;
  forRun: boolean;
  locked: boolean;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  editorOverrides?: object;
  isOversized?: boolean;
  forceRowCount?: number;
  dataTable: DataTable;
  updateHandler: UpdateHandler;
  registrationTables: Array<RegistrationTable>;
  viewer?: boolean;

  // Optional override of default download as CSV behavior
  downloadAsCSV?: () => void;

  // Proxied-through methods / properties
  cid: string;
  indentation: number | undefined;
  index: number;
  tableNamespace: string;
  showExcessLabels: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  computedTable: Array<any>;
  cellColors?: CellColors;
  multiEditing: boolean;
  metadataForRestore?: MetadataForRestore<TableIndex> | null;
  isMaximized: boolean;
  isSplitScreen?: boolean;
  isFocused?: boolean;
}

interface State {
  isManageSourcesModalOpen: boolean;
}

/**
 * This is a legacy results table, not a StructuredTable.
 */
export class DataTableEditable extends React.PureComponent<ConnectedDataTableEditableProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    fileId: PropTypes.string.isRequired,
    forRun: PropTypes.bool.isRequired,
    locked: PropTypes.bool.isRequired,
    editor: PropTypes.object.isRequired,
    editorProps: PropTypes.object.isRequired,
    editorOverrides: PropTypes.object,
    isOversized: PropTypes.bool.isRequired,
    forceRowCount: PropTypes.number,
    dataTable: DATA_TABLE_SHAPE.isRequired,
    updateHandler: PropTypes.func.isRequired,
    registrationTables: PropTypes.arrayOf(REGISTRATION_TABLE_SHAPE.isRequired).isRequired,
    viewer: PropTypes.bool,

    // Optional override of default download as CSV behavior
    downloadAsCSV: PropTypes.func,

    // from DataTableEditableConnector
    // NOTE: TableEditable may be rendered before the connector finishes fetching the state necessary to
    // compute these props, so their values may be empty on initial rendering
    resultsInTable: PropTypes.arrayOf(ASSAY_RECORD_SHAPE.isRequired).isRequired,
    dataTableErrorsByRecordId: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string.isRequired).isRequired)
      .isRequired,
    registrationRecordListsById: PropTypes.objectOf(REGISTRATION_RECORD_LIST_SHAPE).isRequired,
    review: REVIEW_SHAPE,
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
    assayValidationByResultId: PropTypes.objectOf(ASSAY_RESULT_VALIDATION_SHAPE),
    fieldHasValueDictByResultId: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),

    // rowStateManager
    rowStates: PropTypes.arrayOf(
      PropTypes.shape({
        selected: PropTypes.bool.isRequired,
      }).isRequired
    ).isRequired,
    updateRowState: PropTypes.func.isRequired,
    updateEachRowState: PropTypes.func.isRequired,

    // Proxied-through methods / properties
    cid: PropTypes.string,
    indentation: PropTypes.number,
    index: PropTypes.number.isRequired,
    tableNamespace: PropTypes.string.isRequired,
    showExcessLabels: PropTypes.bool.isRequired,
    computedTable: PropTypes.array.isRequired,
    cellColors: PropTypes.object,
    multiEditing: PropTypes.bool.isRequired,
    metadataForRestore: PropTypes.object,
  };

  static defaultProps = {isOversized: false};

  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  override state: State = {
    isManageSourcesModalOpen: false,
  };

  _isMounted = false;

  // getTableWithValidationIndicators is expensive and typically does not change render-to-render, so we memo.
  _memoizedGetTableWithValidationIndicators = memoizeLastCall(
    (fullTable: Table, validationIndicatorGenerationData: ValidationIndicatorGenerationData) =>
      getTableWithValidationIndicators(fullTable, validationIndicatorGenerationData),
    // validationIndicatorGenerationData and fullTable are dynamically constructed, so use deep equality.
    {useDeepEquality: true}
  );

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  override componentDidUpdate(prevProps: ConnectedDataTableEditableProps): void {
    assert(this.props.cid === prevProps.cid, 'Did not expect DataTableEditable cid to change');
  }

  _handleToggleResultSelected = (euid: string, selected: boolean): void => {
    this.props.updateRowState(euid, (rowState) => ({...rowState, selected}));
  };

  _handleToggleAllSelected = (selected: boolean): void => {
    this.props.updateEachRowState((rowState) => ({...rowState, selected}));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _handleImportNewRows = (importedRows: any): void => {
    const newDataTable = insertRowsFromSpreadsheet(this.props.dataTable, importedRows);
    if (!this._isMounted) {
      return;
    }
    this.props.updateHandler(this.props.index, {item: newDataTable}, undefined, {
      [this.props.dataTable.config.name]: {type: 'eval-table'},
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _handleImportUpdatedRows = (importedRows: any): void => {
    const newDataTable = updateRowsFromSpreadsheet(this.props.dataTable, importedRows);
    if (!this._isMounted) {
      return;
    }
    this.props.updateHandler(this.props.index, {item: newDataTable}, undefined, {
      [this.props.dataTable.config.name]: {type: 'eval-table'},
    });
  };

  _handleOpenManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: true});
  };

  _handleCloseManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: false});
  };

  _handleSubmitManageSourcesModal = (
    existingSourceTableUuids: Array<string>,
    newSourceTableUuids: Array<string>
  ): void => {
    const sourceTableUuids = [...existingSourceTableUuids, ...newSourceTableUuids];
    let newDataTable = reactUpdate(this.props.dataTable, {
      config: {
        isWatchingInputTable: {$set: sourceTableUuids.includes(INPUT_SAMPLES_SOURCE_UUID)},
        watchedRegistrationProvenanceUuids: {
          // eslint-disable-next-line no-restricted-syntax
          $set: _.without(sourceTableUuids, INPUT_SAMPLES_SOURCE_UUID),
        },
      },
    });
    newDataTable = autopopulateWithNewSources({
      dataTable: newDataTable,
      stageRuns: this.props.stageRuns,
      registrationTables: this.props.registrationTables,
      registrationRecordListsById: this.props.registrationRecordListsById,
      newSourceTableUuids,
      stageRunSampleById: this.props.stageRunSampleById,
    });
    this.props.updateHandler(this.props.index, {item: newDataTable}, undefined, {
      [this.props.dataTable.config.name]: {type: 'eval-table'},
    });
    this._handleCloseManageSourcesModal();
  };

  _getColumnLabelsForExport(table: TableData): Array<string> {
    return table.columnConstraints.map((columnConstraint, i) => {
      if (columnConstraint && columnConstraint.columnDefinition) {
        return getColumnLabel(columnConstraint.columnDefinition);
      } else {
        return table.columnLabels[i] as string;
      }
    });
  }

  _handleDownloadAsCSV = async (table: TableData, sampleIds: SampleIds): Promise<void> => {
    if (this.props.downloadAsCSV) {
      this.props.downloadAsCSV();
    } else {
      initiateTextDownload(
        await makeTableCSV({
          table: table.table,
          computedTable: this.props.computedTable,
          headerRow: this._getColumnLabelsForExport(table),
          rowLabels: sampleIds,
          rowLabelHeader: DataTableUtil.tableSampleIdHeaderText(this.props.dataTable.config),
        }),
        `${table.name}.csv`
      );
    }
    analytics.deprecatedTrackEvent(RESULTS_ANALYTICS_ACTIONS.DOWNLOADED_DATA_TABLE_AS_CSV, {
      entry_id: this.props.fileId,
    });
  };

  _handleInsertFromSpreadsheet = (): void => {
    BulkAddRowsToDataTableFlow.open({
      onImportRows: this._handleImportNewRows,
      fileId: this.props.fileId,
      dataTableConfig: this.props.dataTable.config,
    });
  };

  _handleUpdateFromSpreadsheet = (): void => {
    const dataTableKeyFields = [];
    if (!this.props.dataTable.config.isStandalone) {
      dataTableKeyFields.push(COLUMN_TYPE.STAGE_RUN_NAME);
    }
    if (this.props.dataTable.config.linkType === FIELD_TYPES.BATCH_LINK.type) {
      dataTableKeyFields.push(COLUMN_TYPE.BATCH_NAME);
    } else if (this.props.dataTable.config.linkType === FIELD_TYPES.BIOENTITY_LINK.type) {
      dataTableKeyFields.push(COLUMN_TYPE.FILENAME);
    }
    BulkUpdateDataTableFlow.open({
      onImportRows: this._handleImportUpdatedRows,
      dataTable: DataTableUtil.populateDataTableComputedValues(
        this.props.dataTable,
        this.props.computedTable
      ),
      dataTableKeyFields,
    });
  };

  _getNumWatchedSources(): number {
    const {isWatchingInputTable, watchedRegistrationProvenanceUuids} = this.props.dataTable.config;
    // Filter out registration tables that are no longer in this entry
    const actualRegistrationProvenanceUuids = watchedRegistrationProvenanceUuids.filter((uuid) =>
      _.findWhere(this.props.registrationTables, {provenanceUuid: uuid})
    );
    return Number(isWatchingInputTable) + actualRegistrationProvenanceUuids.length;
  }

  _renderAddButton = (
    {isCollapsed}: TableHeaderControlsArgs,
    addMenuItems: Array<AddMenuItem>
  ): JSX.Element | null => {
    if (isCollapsed || this._tableIsLocked() || this.props.forRun) {
      return null;
    }
    return (
      <Dropdown
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.LARGE}>
            {addMenuItems.map((addMenuItem) => (
              <Option {...addMenuItem} key={addMenuItem.value} />
            ))}
            <Option
              key="insert"
              icon="upload"
              onSelect={() => {
                this._handleInsertFromSpreadsheet();
              }}
              dataTestAttrs={{
                component: 'DataTableFileLinkContext',
                element: 'importFromSpreadsheetMenuItem',
              }}
            >
              Import from spreadsheet
            </Option>
            <Option
              key="upload"
              isDisabled={this.props.dataTable.rows.length === 0}
              icon="upload"
              onSelect={() => {
                this._handleUpdateFromSpreadsheet();
              }}
            >
              Update from spreadsheet
            </Option>
          </Menu>
        }
      >
        <RichObjectTable.AddButton />
      </Dropdown>
    );
  };

  _getAssayResultIdsByRow(): Array<string | null> | null {
    if (DataTableUtil.isAssayTypeResult(this.props.dataTable.config)) {
      // eslint-disable-next-line no-restricted-syntax
      const existingResultIds = _.pluck(this.props.resultsInTable, 'id');
      return this.props.dataTable.rows.map((row) => (existingResultIds.includes(row.euid) ? row.euid : null));
    }
    return null;
  }

  _renderSetValidationButton(
    table: TableData,
    isLockedOrProtected: boolean,
    multiSelectionBox: MultiSelectionBox | null
  ): JSX.Element | null {
    const assayResultIdsByRow = this._getAssayResultIdsByRow();
    if (!assayResultIdsByRow) {
      return null;
    }
    const resultsById = _.indexBy(this.props.resultsInTable, 'id');
    const assayResultsByRow = assayResultIdsByRow.map((resultId) =>
      resultId ? resultsById[resultId] : null
    );
    const {assaySchema, columns} = this.props.dataTable.config;
    return (
      <FormulaBoxSetValidationButton
        assayResultsByRow={assayResultsByRow}
        hiddenFields={getUnusedSchemaFields(assaySchema, columns)}
        isLockedOrProtected={isLockedOrProtected}
        multiSelectionBox={multiSelectionBox}
        review={this.props.review}
        table={table}
      />
    );
  }

  _renderManageSourcesButton(): JSX.Element {
    return (
      <Tooltip title="Manage sources">
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            disabled={this._tableIsLocked()}
            icon="sources"
            onClick={this._handleOpenManageSourcesModal}
            size={ButtonSize.MINI}
            text={this._getNumWatchedSources().toString()}
          />
        </span>
      </Tooltip>
    );
  }

  _renderExportToCSVButton = (table: TableData, sampleIds: SampleIds): JSX.Element => (
    <SimpleDownloadAsCSVButton
      disabled={!this.props.downloadAsCSV && this.props.dataTable.rows.length === 0}
      onDownloadAsCSV={() => {
        this._handleDownloadAsCSV(table, sampleIds);
      }}
    />
  );

  _renderHeaderControls = (
    args: TableHeaderControlsArgs,
    table: TableData,
    sampleIds: SampleIds
  ): JSX.Element => {
    if (args.isCollapsed) {
      return this._renderExportToCSVButton(table, sampleIds);
    }
    const doRefresh = async (): Promise<void> => {
      await handleRefreshDataTableData({
        entryOrTemplateId: this.props.editorProps.fileId,
        table,
        computedTable: this.props.computedTable,
        onUpdateTable: ({
          updatedCells,
          columnUpdates,
        }: {
          updatedCells: Cells;
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...table, table: updatedCells, columnConstraints};
          this.props.updateHandler(this.props.index, {table: newTable}, undefined, {
            [this.props.dataTable.config.name]: {type: 'eval-table'},
          });
        },
      });
    };

    const refreshTableButton = (
      <span key="updateLookupFieldValuesButton">
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Results table"
          disabled={this._tableIsLocked()}
        />
      </span>
    );
    const manageSourcesButton = <span key="manageSourcesButton">{this._renderManageSourcesButton()}</span>;
    const exportToCSVButton = (
      <span key="exportToCSVButton">{this._renderExportToCSVButton(table, sampleIds)}</span>
    );

    const headerControls = _.compact([
      !this.props.forRun && refreshTableButton,
      !this.props.viewer && exportToCSVButton,
      !this.props.forRun && !this.props.dataTable.config.isStandalone && manageSourcesButton,
    ]);

    return <span className="u-vertical-center-items">{headerControls}</span>;
  };

  _getExtraContextMenuOptionsFunc = (): ReturnType<
    typeof DataTableUtil.getDataTableConfigContextMenuOptions
  > =>
    DataTableUtil.getDataTableConfigContextMenuOptions(this.props.dataTable.config, {
      onMarkAsReported: !this._tableIsLocked() && !this.props.forRun ? this._markAsReported : null,
    });

  _getComputedTable = (): ReturnType<typeof DataTableUtil.inlineErrorsIntoCells> =>
    DataTableUtil.inlineErrorsIntoCells(
      this.props.computedTable,
      this.props.dataTable,
      this.props.dataTableErrorsByRecordId
    );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _markAsReported = (selectionRange: any, isReported: any): void => {
    const table = DataTableUtil.convertDataTableToTable(this.props.dataTable);
    this.props.updateHandler(
      this.props.index,
      {
        table: DataTableUtil.markColumnAsReported(table, selectionRange, isReported),
      },
      undefined,
      {[table.name]: {type: 'eval-table'}}
    );
  };

  _handleDataTableUpdate = (dataTableArg: DataTable): void => {
    this.props.updateHandler(this.props.index, {item: dataTableArg}, undefined, {
      [this.props.dataTable.config.name]: {type: 'eval-table'},
    });
  };

  _canAttachFileToCell = ([i, j]: Array<number>): boolean => {
    const {config} = this.props.dataTable;
    const columnType = getColumnTypeFromSchema(config.columns[j], config.assaySchema);
    return columnType === FIELD_TYPES.BLOB_LINK.type;
  };

  _handleUpdateTable = (
    index: number,
    info: UpdateHandlerInfo,
    entryIdentifier: string,
    tableOp?: TableOpDiffMap
  ): void => {
    analytics.deprecatedTrackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_DATA_TABLE, {
      entry_id: entryIdentifier,
      provenance_id: info.table.provenanceUuid,
    });
    this.props.updateHandler(index, info, undefined, tableOp);
  };

  _getLinkContext(): AnyDataTableLinkContext {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    const dataTableType = DataTableUtil.getDataTableConfigType(this.props.dataTable.config);
    switch (dataTableType) {
      case DATA_TABLE_TYPE.STAGE_RUN_SAMPLE_LINK_TABLE:
        assert(fileType === ModelTypeConstants.STAGE_ENTRY, 'Sample linked DataTable expected stage entry');
        return DataTableStageRunSampleLinkContext;
      case DATA_TABLE_TYPE.BATCH_LINK_TABLE:
        return DataTableBatchLinkContext;
      case DATA_TABLE_TYPE.FILE_LINK_TABLE:
        return DataTableFileLinkContext;
      default:
        return DataTableNoLinkContext;
    }
  }

  _schemaIsArchived(): boolean {
    return isArchived(this.props.dataTable.config.assaySchema);
  }

  _tableIsLocked(): boolean {
    return (
      this._schemaIsArchived() ||
      this.props.locked ||
      !isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE})
    );
  }

  override render(): JSX.Element {
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );
    if (this.props.forRun) {
      Object.assign(allEditorProps, {deleteHandler: null});
    }
    if (this.props.editorOverrides) {
      Object.assign(allEditorProps, this.props.editorOverrides);
    }
    const LinkContext: AnyDataTableLinkContext = this._getLinkContext();

    return (
      <>
        <ModalAnchor open={this.state.isManageSourcesModalOpen}>
          {this.state.isManageSourcesModalOpen && this.props.dataTable.config.linkType && (
            <ManageSourcesModal
              // @ts-expect-error: the link type should be correct
              linkType={this.props.dataTable.config.linkType}
              initialSourceTableUuids={_.compact([
                this.props.dataTable.config.isWatchingInputTable && INPUT_SAMPLES_SOURCE_UUID,
                ...this.props.dataTable.config.watchedRegistrationProvenanceUuids,
              ])}
              onRequestClose={this._handleCloseManageSourcesModal}
              onRequestSubmit={this._handleSubmitManageSourcesModal}
              registrationTables={this.props.registrationTables}
            />
          )}
        </ModalAnchor>
        <LinkContext
          entryId={this.props.fileId}
          dataTable={this.props.dataTable}
          onUpdate={this._handleDataTableUpdate}
          registrationTables={this.props.registrationTables}
          note={this.props.editor.props.note}
        >
          {({
            table,
            sampleIds,
            addMenuItems,
          }: {
            table: Table;
            sampleIds: SampleIds;
            addMenuItems: Array<AddMenuItem>;
          }) => (
            <DataTableConfigContextHandler
              dataTableConfig={this.props.dataTable.config}
              entryId={this.props.fileId}
              forRun={this.props.forRun}
            >
              {({
                additionalFunctions,
                insertableColumnInfos,
                getDisallowedColumnNames,
              }: {
                // see DataTableConfigContextHandler._handleLookupFieldClicked
                additionalFunctions: Array<TableFunction>;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                insertableColumnInfos: Array<any>; // see DataTableUtil.getInsertableColumnInfos
                getDisallowedColumnNames: () => Array<string>;
              }) => {
                table = this._memoizedGetTableWithValidationIndicators(
                  {
                    ...table,
                    nameTooltip: this.props.isOversized ? OVERSIZED_RESULTS_TABLE_TOOLTIP : null,
                  },
                  {
                    assayValidationByResultId: this.props.assayValidationByResultId,
                    fieldHasValueDictByResultId: this.props.fieldHasValueDictByResultId,
                    assayResultIdsByRow: this._getAssayResultIdsByRow(),
                    assaySchema: this.props.dataTable.config.assaySchema,
                    dataTableErrorsByRecordId: this.props.dataTableErrorsByRecordId,
                  }
                );
                return (
                  <TableEditable
                    {...allEditorProps}
                    locked={this._tableIsLocked()}
                    isProtected={this.props.editor.isProtectedAgainstEdit()}
                    editWhileTableLockedWarning={getLockedTableWarningMessage(
                      this._schemaIsArchived()
                        ? LockedTableWarningType.ARCHIVED_SCHEMA
                        : LockedTableWarningType.GENERIC
                    )}
                    headerControls={(args) => this._renderHeaderControls(args, table, sampleIds)}
                    leftHeaderControls={(args) => this._renderAddButton(args, addMenuItems)}
                    // Item props
                    cid={this.props.cid}
                    collapseId={createCollapseId(this.props.fileId, table.name)}
                    table={this.props.forRun ? transformTableForRun(table) : table}
                    index={this.props.index}
                    indentation={this.props.indentation}
                    icon="table"
                    tableNamespace={this.props.tableNamespace}
                    showExcessLabels={this.props.showExcessLabels}
                    computedTable={this._getComputedTable()}
                    cellColors={this.props.cellColors}
                    multiEditing={this.props.multiEditing}
                    metadataForRestore={this.props.metadataForRestore}
                    // Customize some table functionality
                    canAttachFileToCell={this._canAttachFileToCell}
                    convertFileToCellData={convertFileToCellData}
                    hasPlaceholderRows={this.props.dataTable.rows.length === 0}
                    isCollapsible={true}
                    forceCollapsed={Boolean(this.props.isOversized)}
                    forceRowCount={this.props.forceRowCount}
                    rowStates={this.props.rowStates}
                    showFormulaBox={this.props.dataTable.rows.length > 0}
                    additionalFunctions={additionalFunctions}
                    additionalSettings={
                      this.props.forRun
                        ? undefined
                        : [
                            {
                              name: UPDATE_LOOKUP_FIELD_MESSAGE,
                              applySettingToSelection: handleUpdateLookupFieldValues,
                            },
                          ]
                    }
                    renderAdditionalFormulaBoxButtons={this._renderSetValidationButton.bind(
                      this,
                      table,
                      this.props.editor.isProtectedAgainstEdit() || this._tableIsLocked()
                    )}
                    insertableColumnInfos={insertableColumnInfos}
                    disableFormulas={false}
                    selectorsEnabled={false}
                    selectorsLink={DataTableEditable.selectorsLink}
                    openManageSelectorsModal={noop}
                    disableLegacyHeader={true}
                    disableMaximizedTables={true}
                    setMaximizedTable={noop}
                    setSplitScreenTable={noop}
                    isMaximized={this.props.isMaximized}
                    isSplitScreen={this.props.isSplitScreen}
                    isFocused={this.props.isFocused}
                    isLeftAlignColumnHeader={true}
                    getExtraContextMenuOptions={this._getExtraContextMenuOptionsFunc()}
                    getDisallowedColumnNames={getDisallowedColumnNames}
                    tableLabel={MODEL_NAMES.Result}
                    updateHandler={(
                      index: number,
                      info: UpdateHandlerInfo,
                      _selectors?: Optional<Array<Selector>>,
                      tableOp?: TableOpDiffMap
                    ) => {
                      this._handleUpdateTable(index, info, this.props.fileId, tableOp);
                    }}
                    isNestedInParentComponent={this.props.forRun}
                    dataTestAttrs={{component: 'DataTableEditable', element: 'legacyResultsTable'}}
                    disableMergingCells={true}
                    {...forwardSearchResultProps(this.props)}
                  />
                );
              }}
            </DataTableConfigContextHandler>
          )}
        </LinkContext>
      </>
    );
  }
}

export type ConnectedDataTableEditableProps = DataTableEditableProps &
  DataTableEditableEnhancedProps &
  RowStateManagerProps;

export default rowStateManager(DataTableEditableConnector.connect(DataTableEditable));
